import { useEffect, useState } from "react";
import { ArrowUpRight, Search } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import { FadeUp } from "@/components/Reveal";
import { SEO } from "@/lib/seo";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Scholarships() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("");

  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (level) params.level = level;
    api.get("/scholarships", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [q, level]);

  return (
    <div data-testid="scholarships-page">
      <SEO title="Australian University Scholarships for International Students | Vitaliah" description="Search scholarships at Australian universities for international students — filter by study level and university." />
      <Navbar />
      <PageHero
        overline="Funding"
        title="Scholarships, without the noise."
        subtitle="A growing, searchable directory of international-student scholarships at Australian universities. Entries marked as placeholders are being verified."
      />
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <div className="grid sm:grid-cols-3 gap-4 bg-white border border-border p-6" data-testid="scholarship-filters">
              <div className="sm:col-span-2 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-navy/40" />
                <Input data-testid="scholarship-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by scholarship or university" className="rounded-none pl-10 border-input focus-visible:ring-gold" />
              </div>
              <Select value={level || "__all__"} onValueChange={(v) => setLevel(v === "__all__" ? "" : v)}>
                <SelectTrigger data-testid="scholarship-level-filter" className="rounded-none border-input focus:ring-gold">
                  <SelectValue placeholder="Study level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All levels</SelectItem>
                  <SelectItem value="Undergraduate">Undergraduate</SelectItem>
                  <SelectItem value="Postgraduate">Postgraduate</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </FadeUp>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="scholarship-grid">
            {items.map((s, i) => (
              <FadeUp key={s.id || i} delay={(i % 3) * 0.08} className="h-full">
                <div data-testid={`scholarship-card-${i}`} className="flex flex-col h-full bg-white border border-border p-8 hover:border-gold hover:-translate-y-1 hover:shadow-lg transition-[transform,border-color,box-shadow] duration-500">
                  <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">{s.university_name}</p>
                  <h3 className="mt-3 font-serif text-2xl font-medium text-navy leading-snug">{s.name}</h3>
                  <dl className="mt-6 space-y-3 text-sm flex-1">
                    <div><dt className="text-navy/45 text-xs uppercase tracking-wider">Study level</dt><dd className="mt-1 text-navy/80">{s.study_level}</dd></div>
                    <div><dt className="text-navy/45 text-xs uppercase tracking-wider">Eligibility</dt><dd className="mt-1 text-navy/80">{s.eligibility}</dd></div>
                    <div><dt className="text-navy/45 text-xs uppercase tracking-wider">Value</dt><dd className="mt-1 text-navy/80">{s.value}</dd></div>
                    <div><dt className="text-navy/45 text-xs uppercase tracking-wider">Deadline</dt><dd className="mt-1 text-navy/80">{s.deadline}</dd></div>
                  </dl>
                  {s.learn_more_url ? (
                    <a href={s.learn_more_url} target="_blank" rel="noopener noreferrer" data-testid={`scholarship-link-${i}`} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold transition-colors">
                      Learn more <ArrowUpRight className="h-4 w-4" />
                    </a>
                  ) : (
                    <span className="mt-6 text-xs text-navy/40">Official link will be added once verified.</span>
                  )}
                </div>
              </FadeUp>
            ))}
          </div>
          {items.length === 0 && (
            <p data-testid="scholarships-empty" className="mt-20 text-center text-navy/50">No scholarships match your search yet.</p>
          )}
        </div>
      </section>
      <FinalCTA />
      <Footer />
    </div>
  );
}
